import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { X } from '../../lib/icons';
import { hydrater, surHydratation } from '../../lib/store';
import { surDemarrage } from '../../lib/demarrage';
import { useTresorerie, verifierTresorerie } from '../../lib/tresorerieEtat';
import { reconcilierEnAttente } from '../../lib/financeCommandes';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

export default function AppShell() {
  const [query, setQuery] = useState('');
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [donnees, setDonnees] = useState(null);
  const [demarrage, setDemarrage] = useState(null);
  const tresorerie = useTresorerie();
  const enAttente = tresorerie.indeterminees;
  const { pathname } = useLocation();

  useEffect(() => surHydratation(setDonnees), []);
  useEffect(() => surDemarrage(setDemarrage), []);

  // AUDIT C03 : une opération envoyée sans réponse exploitable n'est JAMAIS présentée
  // comme absente. Le serveur est interrogé sur son identifiant d'opération, au
  // chargement, toutes les 30 s puis à chaque vérification de la base. Une sonde de
  // santé verte ne lève pas ce blocage : seul le verdict du serveur le fait.
  const trancher = async (silencieux = false) => {
    const bilan = await reconcilierEnAttente();
    if (silencieux) return bilan;
    if (bilan.confirmees)
      toast.success(`${bilan.confirmees} opération(s) retrouvée(s) en base : elles sont bien enregistrées.`);
    if (bilan.absentes)
      toast.message(
        `${bilan.absentes} opération(s) absente(s) de la base, identifiant désormais VERROUILLÉ : aucune écriture ne pourra plus en sortir.`
      );
    if (bilan.indeterminees)
      toast.error(
        `${bilan.indeterminees} opération(s) au RÉSULTAT INDÉTERMINÉ : la base n’a pas tranché. NE LES RESSAISISSEZ PAS.`
      );
    return bilan;
  };

  useEffect(() => {
    if (donnees?.etat !== 'ok') return;
    trancher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donnees?.etat]);

  // Tant que la base refuse les écritures de caisse, on resonde seul : tout de suite au
  // chargement (le blocage survit au rechargement) puis toutes les 30 s. Le blocage se
  // lève dès que la base redevient apte, sans action de l'utilisateur.
  useEffect(() => {
    if (!tresorerie.bloque) return;
    verifierTresorerie();
    const t = setInterval(() => {
      trancher(true).then(() => verifierTresorerie());
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tresorerie.bloque]);

  // Données serveur non chargées : l'utilisateur doit le savoir, car aucune saisie
  // n'est publiée tant que la lecture n'a pas abouti (protection anti-écrasement).
  const bloque = donnees && donnees.etat !== 'ok' && donnees.session;

  return (
    <div data-testid="app-shell" className="flex h-screen w-full overflow-hidden bg-slate-50">
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {menuOuvert && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-overlay">
          <button
            type="button"
            aria-label="Fermer le menu"
            data-testid="mobile-menu-backdrop"
            onClick={() => setMenuOuvert(false)}
            className="absolute inset-y-0 left-64 right-0 bg-slate-900/50"
          />
          <div
            key={pathname}
            onClick={(e) => {
              if (e.target.closest('a')) setMenuOuvert(false);
            }}
            className="relative h-full w-64 max-w-[85vw] animate-fade-up bg-white shadow-xl"
          >
            <button
              type="button"
              data-testid="mobile-menu-close"
              onClick={() => setMenuOuvert(false)}
              className="absolute right-2 top-4 z-10 flex h-10 w-10 items-center justify-center text-slate-500 hover:text-slate-900"
            >
              <X size={20} />
            </button>
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <Header query={query} onQueryChange={setQuery} onMenuClick={() => setMenuOuvert(true)} />
        {tresorerie.bloque && (
          <div
            data-testid="bandeau-tresorerie"
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b-2 border-red-800 bg-red-600 px-4 py-2 text-xs text-white"
          >
            <strong className="uppercase tracking-[0.08em]">
              {enAttente > 0
                ? 'Opération de trésorerie NON CONFIRMÉE par la base — résultat indéterminé'
                : 'Dernière opération de trésorerie NON CONFIRMÉE par la base'}
            </strong>
            <span data-testid="bandeau-tresorerie-cause" className="font-data font-semibold">
              {tresorerie.message}
            </span>
            <span>
              Tous les règlements sont bloqués : aucun bon ne peut être marqué réglé sans son mouvement de caisse.
              {enAttente > 0
                ? ' Une ou plusieurs opérations attendent le verdict du serveur : NE LES RESSAISISSEZ PAS avant vérification.'
                : ' Reprenez l’opération après vérification de la base.'}
            </span>
            {enAttente > 0 && (
              <span data-testid="commandes-indeterminees" className="font-data font-semibold">
                {enAttente} opération(s) au résultat indéterminé
              </span>
            )}
            <button
              type="button"
              data-testid="verifier-tresorerie"
              disabled={tresorerie.controle}
              onClick={async () => {
                const bilan = await trancher();
                const r = await verifierTresorerie();
                if (!r.ok) toast.error(`Base toujours indisponible : ${r.message}`);
                else if (bilan.indeterminees)
                  toast.error(
                    'Base apte aux écritures, mais le sort de l’opération reste INDÉTERMINÉ : la caisse reste verrouillée, ne ressaisissez pas.'
                  );
                else toast.success('Base de nouveau apte aux écritures de caisse — règlements rouverts');
              }}
              className="border border-white bg-white px-2 py-0.5 font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 disabled:opacity-60"
            >
              {tresorerie.controle ? 'Vérification…' : 'Vérifier la base'}
            </button>
          </div>
        )}
        {!tresorerie.bloque && tresorerie.enCours > 0 && (
          <div
            data-testid="bandeau-tresorerie-en-cours"
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-300 bg-slate-100 px-4 py-2 text-xs text-slate-700"
          >
            <strong className="uppercase tracking-[0.08em]">Enregistrement en cours…</strong>
            <span data-testid="bandeau-tresorerie-en-cours-operations" className="font-data font-semibold">
              {tresorerie.envois.join(', ')}
            </span>
            <span>
              La base n’a pas encore confirmé l’écriture de caisse. Ne cliquez pas une seconde fois : le résultat
              s’affichera dès la réponse du serveur.
            </span>
          </div>
        )}
        {(demarrage?.maintenance || demarrage?.verrou) && (
          <div
            data-testid="bandeau-maintenance-demarrage"
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-xs ${
              demarrage.verrou ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-100 text-amber-900'
            }`}
          >
            <strong>{demarrage.verrou ? 'Écritures bloquées' : 'Maintenance de démarrage'}</strong>
            <span>
              {demarrage.verrou
                ? 'Récupération en cours : aucune saisie n’est enregistrée, la consultation reste possible.'
                : 'Remise à zéro en cours : la consultation reste possible, aucune saisie n’est enregistrée.'}
            </span>
            {demarrage.motif && <span className="font-data">{demarrage.motif}</span>}
          </div>
        )}
        {bloque && (
          <div
            data-testid="bandeau-donnees-serveur"
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-xs ${
              donnees.etat === 'echec' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            <strong>
              {donnees.etat === 'echec' ? 'Données serveur non chargées' : 'Chargement des données serveur…'}
            </strong>
            <span>
              {donnees.etat === 'echec'
                ? `Lecture impossible (${donnees.erreur}). Les enregistrements sont suspendus : aucune donnée du serveur ne peut être écrasée par ce poste.`
                : 'Les enregistrements sont suspendus le temps de la lecture, afin de ne rien écraser sur le serveur.'}
            </span>
            {donnees.ignorees > 0 && (
              <span data-testid="donnees-ignorees" className="font-data font-semibold">
                {donnees.ignorees} modification(s) non enregistrée(s) — à ressaisir après rechargement.
              </span>
            )}
            {donnees.etat === 'echec' && (
              <button
                type="button"
                data-testid="recharger-donnees-serveur"
                onClick={() => hydrater()}
                className="border border-red-300 bg-white px-2 py-0.5 font-semibold transition-colors duration-150 hover:bg-red-100"
              >
                Réessayer le chargement
              </button>
            )}
          </div>
        )}
        <main className="flex-1 overflow-auto p-3 sm:p-6">
          <Outlet context={{ query }} />
        </main>
      </div>
    </div>
  );
}
